import React, { useState } from 'react';
import { Case, TaskPriority } from '../types';
import { CreateTaskRequest } from '../services/tasks';

export interface TaskCreateFormProps {
  cases: Case[];
  onSubmit: (task: CreateTaskRequest) => Promise<void>;
  onCancel: () => void;
}

/**
 * Form for creating new tasks with case selector.
 * Allows selecting a case from dropdown, entering title, priority, and due date.
 * Validates required fields (case selection, title).
 * 
 * Requirements: 7.1, 7.2, 7.3
 */
export const TaskCreateForm: React.FC<TaskCreateFormProps> = ({ cases, onSubmit, onCancel }) => {
  const [form, setForm] = useState({
    caseId: '',
    title: '',
    priority: 'medium' as TaskPriority,
    dueDate: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isValid = form.caseId.trim() !== '' && form.title.trim() !== '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;

    try {
      setSubmitting(true);
      setError(null);
      const request: CreateTaskRequest = {
        caseId: form.caseId,
        title: form.title.trim(),
        priority: form.priority,
        dueDate: form.dueDate || undefined,
      };
      await onSubmit(request);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create task');
    } finally {
      setSubmitting(false);
    }
  };

  // Helper to get student name from case
  const getStudentName = (c: Case): string => {
    return `${c.childFirstName} ${c.childLastName}`;
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '24px',
        maxWidth: '500px',
        width: '90%'
      }}>
        <h3 style={{ marginTop: 0 }}>Create New Task</h3>
        {error && (
          <div style={{
            backgroundColor: '#ffebee',
            color: '#c62828',
            padding: '8px 12px',
            borderRadius: '4px',
            marginBottom: '16px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 500 }}>
              Case (Student) *
            </label>
            <select
              value={form.caseId}
              onChange={e => setForm({ ...form, caseId: e.target.value })}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
              required
            >
              <option value="">Select a case...</option>
              {cases.map(c => (
                <option key={c.caseId} value={c.caseId}>
                  {getStudentName(c)} ({c.caseId.slice(0, 8)}...)
                </option>
              ))}
            </select>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 500 }}>
              Title *
            </label>
            <input
              type="text"
              value={form.title}
              onChange={e => setForm({ ...form, title: e.target.value })}
              placeholder="Enter task title"
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
              required
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 500 }}>
                Priority
              </label>
              <select
                value={form.priority}
                onChange={e => setForm({ ...form, priority: e.target.value as TaskPriority })}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 500 }}>
                Due Date
              </label>
              <input
                type="date"
                value={form.dueDate}
                onChange={e => setForm({ ...form, dueDate: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onCancel}
              style={{
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer',
                backgroundColor: 'white',
                fontSize: '14px'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !isValid}
              style={{
                backgroundColor: '#1976d2',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: submitting || !isValid ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                opacity: submitting || !isValid ? 0.6 : 1
              }}
            >
              {submitting ? 'Creating...' : 'Create Task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/**
 * Prepares a task creation request from form data.
 * Ensures the caseId from the selected case is used.
 * 
 * Property 11: Task creation uses selected caseId
 * Validates: Requirements 7.2, 7.3
 */
export function prepareTaskCreation(
  selectedCaseId: string,
  title: string,
  priority: TaskPriority,
  dueDate?: string
): CreateTaskRequest {
  return {
    caseId: selectedCaseId,
    title: title.trim(),
    priority,
    dueDate: dueDate || undefined,
  };
}
